import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import IconBox from '../../../components/common/icon-box'
import InfoMessage from '../../../components/common/info-message'
import AccountLayout from '../../../components/layout/account-layout'
import { FileDTO, FileDTODocumentTypeEnum, MAX_FILE_SIZE_BYTES, UserDocumentDto } from '@ringo/shared'
import { toast } from 'react-toastify'
import { useMemo, useState } from 'react'
import { useUser } from '../../../common/hooks/auth/useUser'
import { useMutation } from '@tanstack/react-query'
import { useQueryWithLoader } from '../../../components/loader/useQueryWithLoader'
import Modal from '../../../components/common/modal/modal'
import FilePreview from '../../../components/common/file-preview'
import FileUploadField from './file-upload-field'
import Loader from '../../../components/common/Loader'
import { useFileApi } from '../../../common/api-context'
import { useTranslation } from '../../../useTranslation'
import { Trans } from 'react-i18next'

const Authentications = () => {
    const { isVerified: isUserVerified } = useUser()
    const [selectedDocumentType, setSelectedDocumentType] = useState<FileDTODocumentTypeEnum>()
    const [filePreview, setFilePreview] = useState<UserDocumentDto>()
    const [selectedFile, setSelectedFile] = useState<File | null>(null)

    const { t, i18n } = useTranslation()
    const fileApi = useFileApi()

    const allowedFileExtensions = '.pdf, .jpg, .png'

    const customerDataSheetFiles: { [key: string]: string } = {
        en: 'https://www.heimerle-meule.com/fileadmin/ringo_files/Customer-Master-Data-EN.pdf',
        de: 'https://www.heimerle-meule.com/fileadmin/ringo_files/Kundenstammdatenblatt-DE.pdf',
    }

    const documentTypes = useMemo(
        () => Object.values(FileDTODocumentTypeEnum).filter((value) => value !== FileDTODocumentTypeEnum.StammdatenAenderungsNachweis),
        []
    )

    const uploadDocumentMutation = useMutation({
        mutationFn: (file: File) => {
            return fileApi.upload(selectedDocumentType as FileDTODocumentTypeEnum, file as File)
        },
        onSuccess: () => {
            toast.success(t('myAccount.legitimation.fileUploadSuccess'))
        },
    })

    const { data: uploadedDocuments } = useQueryWithLoader('', [uploadDocumentMutation.isSuccess], async () => {
        const result = (await fileApi.userFiles()).data
        return result
    })

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        if (file.size > MAX_FILE_SIZE_BYTES) {
            toast.error(t('myAccount.legitimation.maximumFileSizeIs5MB'))
            return
        }
        setSelectedFile(file)
        uploadDocumentMutation.mutate(file)
    }

    const showPreview = useMutation({
        mutationFn: async (fileId: string) => {
            const result = (await fileApi.getFile(fileId)).data
            setFilePreview(result)
        },
    })

    const handleShowPreview = (documentType: any) => {
        const file = uploadedDocuments?.filter((item) => item.documentType === documentType)[0] as FileDTO

        showPreview.mutate(file.id)
    }

    return (
        <AccountLayout>
            <Modal
                title={t('myAccount.security.filePreview')}
                open={filePreview ? true : false}
                onClose={() => setFilePreview(undefined)}
                dialogClass="max-w-[90%]! h-full pb-[50px]"
            >
                <div className="h-full overflow-y-scroll">{filePreview && <FilePreview file={filePreview as UserDocumentDto} />}</div>
            </Modal>
            <div className="w-4/5 max-tablet:w-11/12">
                <IconBox icon={'circle-check'}>
                    <div className="pt-12 pb-6 px-2 max-tablet:pt-10">
                        <p className="font-bold text-xl text-left leading-none max-tablet:text-base">{t('myAccount.navLink.legitimation')}</p>
                        {isUserVerified ? (
                            <InfoMessage type={'success'} label={t('myAccount.legitimation.accountCheckedAndActivated')} icon="check" />
                        ) : (
                            <InfoMessage type={'error'} label={t('myAccount.legitimation.accountNotApproved')} icon="xmark" />
                        )}
                        {!isUserVerified && (
                            <>
                                <div className="mt-6 max-tablet:mt-5">
                                    <div className="flex">
                                        <FontAwesomeIcon
                                            icon={['fas', 'circle-info']}
                                            className="text-custom_blue text-sm pr-2 align-middle mt-[3px] max-tablet:text-md max-tablet:mt-px"
                                        />
                                        <p className="text-base text-black font-light text-left max-tablet:text-sm">{t('uploadRequiredDocuments')}</p>
                                    </div>
                                    <input
                                        type="file"
                                        id="fileLabel"
                                        onChange={handleFileChange}
                                        accept={allowedFileExtensions}
                                        className="hidden"
                                        disabled={uploadDocumentMutation.isPending}
                                        value={selectedFile ? '' : undefined}
                                    />
                                    <div className="flex  justify-between mt-4 gap-3 ">
                                        {documentTypes.map((documentType) => (
                                            <FileUploadField
                                                text={`option.documentType.${documentType}`}
                                                documentType={documentType}
                                                previewAvailable={uploadedDocuments?.some((item) => item.documentType === documentType) as boolean}
                                                onShowPreview={handleShowPreview}
                                                setSelectedDocumentType={setSelectedDocumentType}
                                            />
                                        ))}
                                    </div>

                                    <p className="text-base text-black font-light text-left max-tablet:text-sm flex flex-col gap-2">
                                        <div>{t('myAccount.legitimation.acceptedFileFormatsInfo')}</div>
                                        <Trans i18nKey="myAccount.legitimation.legitimationUploadHint"></Trans>
                                    </p>

                                    {(uploadDocumentMutation.isPending || showPreview.isPending) && <Loader />}

                                    <hr className="mt-4" />
                                    <div className="flex justify-between pt-4 items-end">
                                        <p className="text-sm leading-none max-tablet:text-xs">{t('myAccount.legitimation.fillCustomerData')}</p>

                                        <a href={customerDataSheetFiles[i18n.language]} download target="_blank">
                                            <p className="text-custom_yellow text-sm leading-none max-tablet:text-xs hover:underline">
                                                {t('myAccount.legitimation.downloadCustomerData')}
                                                <FontAwesomeIcon
                                                    icon={['fas', 'file-arrow-down']}
                                                    className="pl-2 aling-middle text-md mb-px max-tablet:text-xs max-tablet:mb-[0px]"
                                                />
                                            </p>
                                        </a>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </IconBox>
            </div>
        </AccountLayout>
    )
}

export default Authentications
